import { Search, X } from 'lucide-react';
import React from 'react';

import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

import { Separator } from './separator';

interface SearchOption {
  key: string; // Internal identifier for the search option
  label: string; // Display label shown to users
}

export interface SearchInputProps {
  searchOptions: SearchOption[];
  activeSearchType: string;
  searchValues: Record<string, string>;
  onSearchValueChange: (type: string, value: string) => void;
  onSearchTypeChange: (type: string) => void;
  className?: string;
  searchTypeTriggerClassName?: string;
  placeholder?: (activeType: string) => string;
  minWidth?: string;
}

export function SearchInput({
  searchOptions,
  activeSearchType,
  searchValues,
  onSearchValueChange,
  onSearchTypeChange,
  className = 'max-w-2xl min-w-fit',
  searchTypeTriggerClassName = 'w-full',
  placeholder = () => `Type to search`,
  minWidth = '300px',
}: SearchInputProps) {
  const currentValue = searchValues[activeSearchType] || '';

  const handleSearchTypeChange = (newKey: string) => {
    // Copy the current search value to the new search type
    // This preserves user input when switching fields
    const currentSearchValue = searchValues[activeSearchType] || '';
    if (currentSearchValue) {
      onSearchValueChange(newKey, currentSearchValue);
    }

    // Set the new active type
    onSearchTypeChange(newKey);
  };

  const handleClearSearch = () => {
    onSearchValueChange(activeSearchType, '');
  };

  return (
    <div
      className={`flex items-center border border-input rounded-md focus-within:ring-1 focus-within:ring-ring focus-within:border-ring transition-all overflow-hidden ${className}`}
    >
      {searchOptions.length > 1 && (
        <>
          <Select value={activeSearchType} onValueChange={handleSearchTypeChange}>
            <SelectTrigger
              className={`${searchTypeTriggerClassName} border-0! ring-0! ring-offset-0! focus:ring-0! focus:ring-offset-0! outline-hidden! rounded-none! hover:bg-transparent gap-2`}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {searchOptions.map(option => (
                <SelectItem key={option.key} value={option.key}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Separator orientation="vertical" className="h-10" />
        </>
      )}
      <div className="relative flex-1">
        <Input
          placeholder={placeholder(activeSearchType)}
          value={currentValue}
          onChange={e => onSearchValueChange(activeSearchType, e.target.value)}
          className="pl-3 pr-10 border-0 ring-0 ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 outline-hidden rounded-none"
          style={{ minWidth }}
        />
        {currentValue ? (
          <button
            onClick={handleClearSearch}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground z-10"
            title="Clear search"
          >
            <X className="w-4 h-4" />
          </button>
        ) : (
          <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4 z-10 pointer-events-none" />
        )}
      </div>
    </div>
  );
}
